// STEP STUDIO-OVERLAY-UX-FIX-1-STEP3 · 통합 Overlay 설정 화면 (프리셋 · 동시 표시 · 공지/타이머 · 배치 편집).
//   COMBINED-PRESET-1: 적용 대상(자사라이브 / 외부 LIVE용 미리보기) · 저장 범위 · 표시 방식(개별/통합) 추가.
//   기존 OverlayPresetPicker · OverlayTimerControl · 공지 편집기 · 배치 편집 엔진 REUSE.
import React, { useState } from 'react';
import { X, Layers, Move, Megaphone, Clock, Eye, EyeOff, Save, LayoutGrid } from 'lucide-react';
import { cardOrientation, pastOrientation, displayMode, combinedOrientation, combinedItems } from '@/lib/overlayLayout';
import { QR_DURATIONS } from '@/hooks/useOverlayShowCombo';
import { useStudioNoticeHidden } from '@/components/seller/live/StudioOverlayStage';

const chip = (on) => `min-h-[44px] px-3 rounded-xl text-[12px] font-black border-2 transition-colors disabled:opacity-40 ${
  on ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-white/5 text-black dark:text-white border-black/10 dark:border-white/10'}`;

const Section = ({ title, icon: Icon, children, testid }) => (
  <section className="space-y-2.5 pt-4 first:pt-0" data-testid={testid}>
    <p className="text-[13px] font-black text-black dark:text-white inline-flex items-center gap-1.5">
      <Icon size={15} className="text-[#FF3B30]" /> {title}
    </p>
    {children}
  </section>
);

const Choice = ({ options, value, onPick, testid, cols = 2 }) => (
  <div className={`grid gap-2 ${cols === 4 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={String(v)} type="button" onClick={() => onPick(v)} className={`${chip(value === v)} leading-tight py-1.5`}
              data-testid={`${testid}-${v}`}>{l}</button>
    ))}
  </div>
);

const ToggleRow = ({ label, on, onClick, disabled, reason, testid }) => (
  <div className="rounded-xl border border-black/10 dark:border-white/10 px-3 py-2.5" data-testid={`${testid}-row`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-[13px] font-bold text-black dark:text-white">{label}</span>
      <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on}
              className={`${chip(on)} inline-flex items-center gap-1.5 min-w-[84px] justify-center`} data-testid={testid}>
        {on ? <Eye size={14} /> : <EyeOff size={14} />} {on ? 'ON' : 'OFF'}
      </button>
    </div>
    {reason && <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5 leading-snug" data-testid={`${testid}-reason`}>{reason}</p>}
  </div>
);

const ShowSection = ({ sc }) => {
  const p = sc.prefs;
  const kind = !p.size_table && !p.qr ? 'product' : p.size_table && p.qr ? 'st_qr' : p.size_table ? 'st' : null;
  return (
    <>
      <Choice options={[['product', '상품만'], ['st', '상품 + 상품설명'], ['st_qr', '상품 + 상품설명 + QR']]} value={kind}
              onPick={sc.quick} testid="ovs-quick" cols={3} />
      <ToggleRow label="상품 카드" on={sc.productOn} onClick={sc.toggleProduct} disabled={!sc.productToggleable}
                 reason={!sc.productToggleable ? sc.pending : null} testid="ovs-toggle-product" />
      <ToggleRow label="상품설명 (사이즈·소재·기타)" on={p.size_table} onClick={() => sc.update({ size_table: !p.size_table })}
                 reason={p.size_table ? sc.stReason : null} testid="ovs-toggle-size-table" />
      <ToggleRow label="QR 코드" on={p.qr} onClick={() => sc.update({ qr: !p.qr })}
                 reason={p.qr ? sc.qrReason : null} testid="ovs-toggle-qr" />
      <div className="flex items-center gap-2 flex-wrap" data-testid="ovs-qr-duration">
        <span className="text-[11px] font-black text-gray-500">QR 표시 시간</span>
        {QR_DURATIONS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => sc.update({ qr_duration: v })} className={chip(p.qr_duration === v)}
                  data-testid={`ovs-qr-duration-${v}`}>{l}</button>
        ))}
      </div>
    </>
  );
};

const CombinedItems = ({ ol, orient }) => {
  const it = combinedItems(ol.layout);
  const kind = `${it.size_table ? 'st' : ''}${it.qr ? 'qr' : ''}` || 'product';
  const map = { product: [false, false], qr: [false, true], st: [true, false], stqr: [true, true] };
  return (
    <Choice options={[['product', '상품만'], ['qr', '상품 + QR'], ['st', '상품 + 상품설명'], ['stqr', '상품 + 상품설명 + QR']]}
            value={kind} cols={4} testid="ovs-combined-items"
            onPick={(k) => ol.setTop(orient, { combined_items: { size_table: map[k][0], qr: map[k][1] } })} />
  );
};

const SaveStatus = ({ ol, isTestMode, canBroadcast }) => (
  <div className="space-y-2" data-testid="ovs-save-scope">
    {!isTestMode && (
      <Choice options={[['default', '판매자 기본'], ['broadcast', '이 방송만']]} value={ol.saveScope}
              onPick={(v) => (v === 'broadcast' && !canBroadcast ? null : ol.setSaveScope(v))} testid="ovs-scope" />
    )}
    <p className="text-[11px] text-gray-500 leading-snug" data-testid="ovs-save-status">
      {isTestMode ? '테스트 모드 · 이 브라우저에만 자동 저장돼요 (서버 저장 없음).'
        : ol.saveScope === 'broadcast' ? '변경 즉시 이 방송에만 자동 저장 · 판매자 기본값은 그대로예요.'
          : '변경 즉시 판매자 기본으로 자동 저장 · 이 방송 설정이 없는 방송에 자동 적용돼요.'}
      {' '}불러오기 순서: 이 방송 설정 → 판매자 기본 → 미니 프리셋. 테스트 미리보기에는 자동 적용되며 실제 외부 송출 적용은 별도 연결 예정이에요.
    </p>
  </div>
);

export const OverlaySettingsSheet = ({ open, onClose, sc, ol, orient, presetPicker, selfToggle, liveNotice, timerNode,
                                       isLive, isTestMode, canBroadcast, onOpenNotice, onClearNotice, onEditLayout,
                                       extPreview = false, setExtPreview = null }) => {
  const [target, setTarget] = useState('external');
  const [noticeHidden, setNoticeHidden] = useStudioNoticeHidden();
  if (!open) return null;
  const combined = displayMode(ol.layout) === 'combined';
  const co = combined ? combinedOrientation(ol.layout) : cardOrientation(ol.layout);
  const setCo = (v) => (combined ? ol.setTop(orient, { combined_orientation: v }) : ol.setCardOrientation(orient, v, sc.combo));
  return (
    <div className="fixed inset-0 z-[55]" data-testid="overlay-settings-sheet">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} data-testid="overlay-settings-backdrop" />
      <div className="absolute inset-x-0 bottom-0 max-h-[88vh] lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[480px] lg:max-h-none flex flex-col rounded-t-2xl lg:rounded-none bg-white dark:bg-[#141414] animate-in slide-in-from-bottom duration-200">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-black/5 dark:border-white/5">
          <p className="text-sm font-black text-black dark:text-white inline-flex items-center gap-2">
            <Layers size={16} className="text-[#FF3B30]" /> 화면 프리셋 · Overlay
          </p>
          <button type="button" onClick={onClose} className="p-2 -m-1" aria-label="닫기" data-testid="overlay-settings-close"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 divide-y divide-black/5 dark:divide-white/5">
          <Section title="적용 대상" icon={LayoutGrid} testid="ovs-target">
            <Choice options={[['self', '자사라이브'], ['external', '외부 LIVE용 미리보기']]} value={target} onPick={setTarget} testid="ovs-target" />
          </Section>
          {target === 'self' ? (
            <Section title="자사라이브 소비자 화면" icon={Eye} testid="ovs-self">
              {selfToggle}
              <ul className="text-[11px] text-gray-500 leading-snug space-y-0.5" data-testid="ovs-self-policy">
                <li>· 하단 클릭형 상품 카드·상품 목록은 항상 표시돼요.</li>
                <li>· 영상 위 상품 이미지·가격 카드 / QR / 지난 상품 슬라이더는 기본 OFF (중복 방지).</li>
                <li>· 공지·타이머 표시는 기존 방식 그대로예요.</li>
                <li>· 이 설정은 외부 LIVE용 Overlay 설정에 영향을 주지 않아요.</li>
              </ul>
            </Section>
          ) : (
            <>
              {setExtPreview && (
                <Section title="판매자 화면 미리보기" icon={Eye} testid="ovs-ext-preview">
                  <ToggleRow label="외부 송출용 상품 Overlay 미리보기" on={extPreview} onClick={() => setExtPreview(!extPreview)}
                             testid="ovs-ext-preview-toggle" />
                  <p className="text-[11px] text-gray-500 leading-snug">판매자 Studio 화면에서만 보이는 확인용이에요. 닫아도 실제 송출 Overlay 설정은 바뀌지 않아요.</p>
                </Section>
              )}
              <Section title="저장 범위" icon={Save} testid="ovs-scope-section">
                <SaveStatus ol={ol} isTestMode={isTestMode} canBroadcast={canBroadcast} />
              </Section>
              <Section title="표시 방식" icon={Layers} testid="ovs-display-mode">
                <Choice options={[['separate', '개별 표시'], ['combined', '통합 카드']]} value={displayMode(ol.layout)}
                        onPick={(v) => ol.setTop(orient, { display_mode: v })} testid="ovs-mode" />
              </Section>
              <Section title="상품 프리셋" icon={Layers} testid="ovs-presets">{presetPicker}</Section>
              <Section title="카드 형태" icon={Layers} testid="ovs-card-shape">
                <Choice options={[['horizontal', combined ? '통합 가로형' : '가로형 카드'], ['vertical', combined ? '통합 세로형' : '세로형 카드']]}
                        value={co} onPick={setCo} testid="ovs-card" />
                <Choice options={[['horizontal', '지난 상품 가로형'], ['vertical', '지난 상품 세로형']]} value={pastOrientation(ol.layout)}
                        onPick={(v) => ol.setPastOrientation(orient, v, sc.combo)} testid="ovs-past" />
              </Section>
              <Section title={combined ? '통합 카드 포함 항목' : '포함 항목 · 동시 표시'} icon={Eye} testid="ovs-show">
                {combined ? <CombinedItems ol={ol} orient={orient} /> : <ShowSection sc={sc} />}
                <p className="text-[11px] text-gray-500 leading-snug">데이터가 없는 상품설명·QR은 방송 화면에 표시되지 않아요.{combined ? ' 지난 상품·공지·타이머는 통합 카드와 별도로 표시돼요.' : ''}</p>
              </Section>
              <Section title="공지 · 타이머" icon={Megaphone} testid="ovs-notice-timer">
                <div className="rounded-xl border border-black/10 dark:border-white/10 px-3 py-2.5 space-y-2">
                  <p className="text-[12px] font-bold text-black dark:text-white truncate" data-testid="ovs-notice-status">
                    {liveNotice?.text || liveNotice?.image_url ? `공지중 · ${liveNotice.text || '이미지 공지'}` : '표시 중인 공지 없음'}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" onClick={onOpenNotice} disabled={!isLive} className={`${chip(false)} flex-1`} data-testid="ovs-notice-open">공지 관리</button>
                    {(liveNotice?.text || liveNotice?.image_url) && (
                      <button type="button" onClick={onClearNotice} className={`${chip(false)} text-[#FF3B30]`} data-testid="ovs-notice-clear">공지 내리기</button>
                    )}
                    <button type="button" onClick={() => setNoticeHidden(!noticeHidden)} className={chip(noticeHidden)}
                            aria-pressed={noticeHidden} data-testid="ovs-notice-seller-hide">
                      {noticeHidden ? '판매자 화면 보이기' : '판매자 화면 감추기'}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500" data-testid="ovs-notice-seller-hide-hint">외부 송출은 유지, 판매자 화면에서만 숨김</p>
                  {!isLive && <p className="text-[11px] text-gray-500">LIVE 시작 후 공지·타이머를 운영할 수 있어요.</p>}
                </div>
                <div className="inline-flex items-center gap-1.5 text-[12px] font-black text-black dark:text-white"><Clock size={14} className="text-[#FF3B30]" /> 카운트다운 타이머</div>
                {timerNode}
              </Section>
            </>
          )}
        </div>
        {target === 'external' && (
          <div className="px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-black/5 dark:border-white/5">
            <button type="button" onClick={onEditLayout}
                    className="w-full min-h-[50px] rounded-xl bg-black text-white dark:bg-white dark:text-black text-[14px] font-black inline-flex items-center justify-center gap-2"
                    data-testid="ovs-layout-edit">
              <Move size={16} /> 위치·크기 배치 편집
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverlaySettingsSheet;
